'use client'
import { getGoogleDrivePickerConfigAction, startGoogleDriveOAuthAction } from '@/server-actions/knowledge-sources'
import type { KnowledgeAgentRef, KnowledgeSourceProvider } from '@/lib/knowledge-sources/types'
import { chooseGoogleDriveFiles, loadGooglePicker } from './google-picker'
import { openOAuthPopup } from '@/lib/oauth/popup'

export type KnowledgeSourceChooseFiles = () => Promise<{ files: Array<{ id: string; name: string }>; selectionId: string | null }>

/** Browser-side provider boundary: the shared dialog only connects accounts and selects files. */
export interface KnowledgeSourceProviderFlow {
  connect(input: { returnTo: 'integrations' | 'knowledge-base'; agent?: KnowledgeAgentRef; connectionId?: string; popup?: boolean }): Promise<void | { connectionId: string }>
  prepareSelection(connectionId: string): Promise<{ chooseFiles: KnowledgeSourceChooseFiles; suggestedName?: string }>
}

/** Adding a provider requires its own flow here; no Google SDK or configuration reaches shared UI. */
export const knowledgeSourceProviderFlows: Readonly<Record<KnowledgeSourceProvider, KnowledgeSourceProviderFlow>> = {
  google_drive: {
    async connect(input) {
      if (input.popup) return connectGoogleDrivePopup()
      const result = await startGoogleDriveOAuthAction(input)
      if ('status' in result) throw new Error(result.errorMessage)
      window.location.assign(result.url)
    },
    async prepareSelection(connectionId) {
      const [, config] = await Promise.all([loadGooglePicker(), getGoogleDrivePickerConfigAction({ connectionId })])
      if ('status' in config) throw new Error(config.errorMessage)
      return { chooseFiles: () => chooseGoogleDriveFiles(config), suggestedName: config.firstName }
    },
  },
}

function connectGoogleDrivePopup(): Promise<{ connectionId: string }> {
  const popupId = crypto.randomUUID()
  return new Promise((resolve, reject) => {
    openOAuthPopup({ url: `/api/auth/google/knowledge-source/start?popupId=${encodeURIComponent(popupId)}`, name: `knowledge-source-${popupId}`,
      acceptMessage: data => data.knowledge_source_popup === popupId,
      onMessage: data => {
        if (typeof data.knowledgeConnection === 'string') resolve({ connectionId: data.knowledgeConnection })
        else reject(new Error('Google Drive authorization failed.'))
      },
      onError: () => reject(new Error('Google Drive authorization could not open.')),
      onTimeout: () => reject(new Error('Google Drive authorization timed out.')),
    })
  })
}
